/**
 * Shared chart theme utilities for Recharts components
 * Provides dark mode aware colors and responsive chart heights
 */

/**
 * Get chart theme colors based on current dark mode state
 * @returns {object} Theme colors for Recharts components
 */
export const getChartTheme = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    isDark,
    gridColor: isDark ? '#374151' : '#E5E7EB',       // gray-700 / gray-200
    textColor: isDark ? '#9CA3AF' : '#6B7280',        // gray-400 / gray-500
    tooltipBg: isDark ? '#1F2937' : '#FFFFFF',        // gray-800 / white
    tooltipBorder: isDark ? '#374151' : '#E5E7EB',    // gray-700 / gray-200
    tooltipText: isDark ? '#F9FAFB' : '#111827',      // gray-50 / gray-900
    labelColor: isDark ? '#D1D5DB' : '#374151',       // gray-300 / gray-700
  };
};

/**
 * Chart color palette - chosen for visibility in both light and dark modes
 */
export const CHART_COLORS = {
  primary: '#007A7A',
  primaryLight: '#009999',
  warning: '#F18F01',
  success: '#2E7D32',
  secondary: '#7B1FA2',
  gray: '#6B7280',
  danger: '#DC2626',
};

/**
 * Color palette for denomination chart series.
 * Mapped by index since denomination names come dynamically from the API.
 * Designed for visibility in both light and dark modes.
 */
export const DENOMINATION_COLORS = [
  CHART_COLORS.primary,      // $5,500
  CHART_COLORS.primaryLight,  // $11,000
  CHART_COLORS.warning,       // $27,000
  CHART_COLORS.success,       // $55,000
  CHART_COLORS.secondary,     // $105,000
  CHART_COLORS.gray,          // overflow / Other
  CHART_COLORS.danger,        // extra if more tiers appear
  '#0EA5E9',                  // sky-500
];

/**
 * Get responsive chart height based on viewport width
 * @returns {number} Chart height in pixels
 */
export const getChartHeight = () => {
  const width = window.innerWidth;
  if (width < 640) return 220;       // mobile
  if (width < 1024) return 260;      // tablet
  if (width < 2560) return 300;      // desktop
  if (width < 3840) return 340;      // QHD
  return 400;                        // 4K
};

/**
 * Format a number as compact currency (e.g., $1.2M, $450K, $5,000)
 * Used for chart axis labels where space is limited
 * @param {number} value
 * @returns {string}
 */
export const formatCompactCurrency = (value) => {
  if (value === 0) return '$0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
};

/**
 * Format a number as full currency for tooltips
 * @param {number} value
 * @returns {string}
 */
export const formatFullCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

/**
 * Custom tooltip wrapper component styles (inline, for Recharts custom tooltip)
 * @param {object} theme - from getChartTheme()
 * @returns {object} CSS style object
 */
export const getTooltipStyle = (theme) => ({
  backgroundColor: theme.tooltipBg,
  border: `1px solid ${theme.tooltipBorder}`,
  borderRadius: '12px',
  padding: '12px 16px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  color: theme.tooltipText,
  fontSize: '13px',
  lineHeight: '1.5',
});
